import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../styles/SubscriptionContainer.module.scss';
import { usePurchaseSubscriptionContext } from '../../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import SubscriptionAppBar from '../../../components/SubscriptionAppBar';
import Button from '../../../../../components/Button';
import { centsToDollarsNumber } from '../../../../../lib/util';
import { inAppCancellation as events } from '../../../../../lib/analytics/events';
import { useZendeskCancellationTicket } from '../../hooks/useZendesk';
import CancelConfirmationModal from '../../../components/CancelConfirmationModal';
import { useLocation, useHistory } from 'react-router-dom';
import * as gqlTypes from '../../../../../types/gql-op-types';
import { BaseInAppCancellationState } from '../../util/types';
import classNames from 'classnames';
import AppPaths from '../../../../../AppPaths';
import { useMutation } from '@apollo/client';
import { claimFreeMonthMutation, claimTwoMonthsHalfOffMutation } from '../../../../../graphql-operations';
import Loading from '../../../../../components/Loading';
import ErrorMessage from '../../../../../components/ErrorMessage';
import ErrorScreen from '../../../components/ErrorScreen/ErrorScreen';

export interface SpecialOffersState extends BaseInAppCancellationState {
  selectedCancelReason: gqlTypes.zendeskCancellationReason;
  otherText?: string;
}

export enum SpecialOfferKey {
  ONE_MONTH = 'ONE_MONTH_FREE',
  TWO_MONTHS = '50_PERCENT_OFF_TWO_MONTHS',
}

export default function SpecialOffers() {
  const { t } = useTranslation();
  const { device } = usePurchaseSubscriptionContext();
  const history = useHistory();
  const location = useLocation<SpecialOffersState>();
  const [error, setError] = useState('');
  const { selectedCancelReason, subscriptionCategory, otherText } = location.state;

  interface SpecialOffer {
    key: SpecialOfferKey;
    label: string;
    price: number | string;
    originalPrice?: number;
    description: string;
  }
  const [selectedOffer, setSelectedOffer] = useState<SpecialOfferKey>(SpecialOfferKey.ONE_MONTH);
  const petName = device.pet!.name;
  events.pageLoad({ petId: device.pet!.id, subscriptionCategory }, 'Special Offers');

  const monthlyPrice = centsToDollarsNumber(device.subscription?.subscriptionOption?.priceInCents ?? 0);
  const offers: SpecialOffer[] = [
    {
      key: SpecialOfferKey.ONE_MONTH,
      label: t('one_month_free'),
      price: 0,
      originalPrice: monthlyPrice,
      description: t('save_amount_over_your_next_two_months', { amount: monthlyPrice }),
    },
    {
      key: SpecialOfferKey.TWO_MONTHS,
      label: t('50_percent_off_next_two_months'),
      price: t('amount_per_month', { amount: (monthlyPrice / 2).toFixed(2) }),
      description: t('save_amount_over_your_next_two_months', { amount: monthlyPrice }),
    },
  ];
  // Sometimes, this page loads halfway down the screen. This should mitigate those instances.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [freeMonthMutation, freeMonthMutationState] = useMutation<
    gqlTypes.ECOMMERCE_claimFreeMonth,
    gqlTypes.ECOMMERCE_claimFreeMonthVariables
  >(claimFreeMonthMutation, {
    onCompleted: (data) => {
      const success = !!data.claimFreeMonth?.success;
      if (!success) {
        setError(t('failed_to_claim_free_month_contact_customer_support'));
      } else {
        history.push({
          pathname: AppPaths.Subscription.SpecialOffersConfirmed(device.moduleId),
          state: { selectedOffer },
          search: window.location.search,
        });
      }
    },
  });

  const [claimTwoMonthsMutation, claimTwoMonthsMutationState] = useMutation<
    gqlTypes.ECOMMERCE_claimTwoMonthsHalfOffDiscount,
    gqlTypes.ECOMMERCE_claimTwoMonthsHalfOffDiscountVariables
  >(claimTwoMonthsHalfOffMutation, {
    onCompleted: (data) => {
      const success = !!data.claimTwoMonthsHalfOff?.success;
      if (!success) {
        setError(
          data.claimTwoMonthsHalfOff?.reasonForFailure
            ? t('error_reason', { reason: data.claimTwoMonthsHalfOff?.reasonForFailure })
            : t('unable_to_claim_two_months_at_50_percent_off_contact_customer_support'),
        );
      } else {
        history.push({
          pathname: AppPaths.Subscription.SpecialOffersConfirmed(device.moduleId),
          state: { selectedOffer },
          search: window.location.search,
        });
      }
    },
  });

  const { mutation, loading } = useZendeskCancellationTicket({
    analyticsEvent: events.cancelFromSpecialOffersPage,
    selectedCancelReason,
    device,
    subscriptionCategory,
    otherText,
  });

  const claimSpecialOffer = () => {
    events.specialOffersClaim({ petId: device.pet?.id, subscriptionCategory }, selectedOffer);
    if (selectedOffer === SpecialOfferKey.ONE_MONTH) {
      freeMonthMutation({
        variables: {
          input: {
            moduleId: device.moduleId,
          },
        },
      });
    } else if (selectedOffer === SpecialOfferKey.TWO_MONTHS) {
      claimTwoMonthsMutation({
        variables: {
          input: {
            recurlySubscriptionId: device.subscription!.id,
            moduleId: device.moduleId,
          },
        },
      });
    }
  };

  if (loading || freeMonthMutationState.loading || claimTwoMonthsMutationState.loading) {
    return <Loading />;
  }

  if (!device.subscription || !device.subscription?.id) {
    return <ErrorScreen />;
  }

  const handleCancel = () => {
    events.specialOffersContinue({ petId: device.pet?.id, subscriptionCategory });
    if (!device.subscription?.id) {
      throw new Error('Something went wrong.');
    }
    mutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription.id,
          cancellationReason: selectedCancelReason,
          additionalText: otherText,
        },
      },
    });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.specialOffersBack({ petId: device.pet?.id, subscriptionCategory })}
          closeButtonAnalyticsEvent={() => events.specialOffersClose({ petId: device.pet?.id, subscriptionCategory })}
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.largeHeader}>
            <h1>{t('special_offers_just_for_you')}</h1>
          </div>
          <p>
            {t(
              'we_want_to_keep_you_and_pet_name_in_the_pack_so_you_have_more_time_to_explore_step_counting_sleep_tracking_and_pet_name_rank',
              {
                petName,
              },
            )}
            <br />
            <br />
            {t('choose_the_offer_that_works_best_for_you')}
          </p>
          {error ? (
            <ErrorMessage errors={[error]} />
          ) : (
            <div className={styles.offerContainer}>
              {offers.map((offer, idx) => {
                return (
                  <div
                    className={classNames(styles.offer, { [styles.selected]: selectedOffer === offer.key })}
                    onClick={() => setSelectedOffer(offer.key)}
                  >
                    <div className={styles.title}>
                      <div className={styles.label}>{offer.label}</div>
                      <div className={styles.offerNumberLabel}>{t('offer_number', { number: idx + 1 })}</div>
                    </div>
                    <div className={styles.price}>
                      {'$'}
                      {offer.price}{' '}
                      {offer.originalPrice && (
                        <span>
                          {'$'}
                          {offer.originalPrice}
                        </span>
                      )}
                    </div>
                    <div className={styles.description}>{offer.description}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.buttonContainer}>
          <Button className={styles.button} disabled={!selectedOffer} onClick={claimSpecialOffer}>
            {t('claim_special_offer')}
          </Button>
          <CancelConfirmationModal
            onContinue={() => handleCancel()}
            pageName={t('manage_membership_special_offers')}
            petId={device.pet!.id}
            trigger={
              <Button className={styles.button} tertiary={true}>
                {t('continue_with_cancellation')}
              </Button>
            }
          />
        </div>
      </div>
    </>
  );
}
